/**
 * Bonus Hooks
 * React Query hooks for bonus-related operations
 */

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  fetchUserBonus,
  fetchBonuses,
  fetchBonusRedeem,
  fetchBonusHistory,
  claimBonus,
  redeemBonusCode,
} from '@/services/api/bonusService';
import { getUID } from '@/utils';
import { useToast } from './useToast';

/**
 * Hook to fetch user bonus data
 */
export const useUserBonus = (enabled = true) => {
  const uid = getUID();

  return useQuery({
    queryKey: ['userBonus', uid],
    queryFn: fetchUserBonus,
    enabled: enabled && !!uid,
    staleTime: 30000, // 30 seconds
    retry: 2,
    retryDelay: 1000,
  });
};

/**
 * Hook to fetch bonuses (daily, weekly, monthly)
 */
export const useBonuses = (enabled = true) => {
  const uid = getUID();

  return useQuery({
    queryKey: ['bonuses', uid],
    queryFn: fetchBonuses,
    enabled: enabled && !!uid,
    staleTime: 30000, // 30 seconds
    retry: 2,
    retryDelay: 1000,
  });
};

/**
 * Hook to fetch bonus redeem data
 */
export const useBonusRedeem = (enabled = true) => {
  const uid = getUID();

  return useQuery({
    queryKey: ['bonusRedeem', uid],
    queryFn: fetchBonusRedeem,
    enabled: enabled && !!uid,
    staleTime: 30000, // 30 seconds
    retry: 2,
    retryDelay: 1000,
  });
};

/**
 * Hook to fetch bonus history
 */
export const useBonusHistory = (enabled = true) => {
  const uid = getUID();

  return useQuery({
    queryKey: ['bonusHistory', uid],
    queryFn: fetchBonusHistory,
    enabled: enabled && !!uid,
    staleTime: 60000, // 1 minute
    retry: 2,
    retryDelay: 1000,
  });
};

/**
 * Hook to claim bonus
 */
export const useClaimBonus = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: claimBonus,
    onSuccess: (data) => {
      // Play success sound
      const audio = new Audio('/sounds/magic-spell.mp3');
      audio.play().catch((error) => console.error('Error playing audio:', error));

      showSuccess('Bonus claimed successfully');
      
      // Invalidate and refetch bonus-related queries
      queryClient.invalidateQueries({ queryKey: ['userBonus'] });
      queryClient.invalidateQueries({ queryKey: ['bonuses'] });
      queryClient.invalidateQueries({ queryKey: ['bonusHistory'] });
      
      // Reload page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    onError: (error) => {
      showError(error.message || 'Failed to claim bonus');
    },
  });
};

/**
 * Hook to redeem bonus code
 */
export const useRedeemBonusCode = () => {
  const queryClient = useQueryClient();
  const { showSuccess, showError } = useToast();

  return useMutation({
    mutationFn: redeemBonusCode,
    onSuccess: (data) => {
      showSuccess(data.message || 'Bonus code redeemed successfully');
      
      // Invalidate and refetch bonus-related queries
      queryClient.invalidateQueries({ queryKey: ['userBonus'] });
      queryClient.invalidateQueries({ queryKey: ['bonusRedeem'] });
      
      // Reload page after a short delay
      setTimeout(() => {
        window.location.reload();
      }, 1000);
    },
    onError: (error) => {
      showError(error.message || 'Failed to redeem bonus code');
    },
  });
};
